import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowRight, Eye, EyeOff, Hourglass, Mail, Sparkles } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { AuthLayout } from "@/features/auth/auth-layout";
import { probeSession } from "@/hooks/data";
import { api } from "@/lib/api/client";
import { isApiError } from "@/lib/api/errors";
import { adoptSession } from "@/lib/auth";
import { config } from "@/lib/config";
import { useT } from "@/lib/i18n";

const search = z.object({ redirect: z.string().optional(), expired: z.boolean().optional() });

export const Route = createFileRoute("/login")({
  validateSearch: search,
  beforeLoad: async ({ context }) => {
    // already signed in: straight to the app
    const me = await probeSession(context.queryClient).catch(() => null); // QA F10: no 401 probe
    if (me) throw redirect({ to: "/home" });
  },
  head: () => ({ meta: [{ title: "Sign in · Malfak" }] }),
  component: LoginPage,
});

const DEMO = { email: "owner@malfak.local", password: "malfak-demo-123" };

function LoginPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const { redirect: to, expired } = Route.useSearch();
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ALP-85: sign-up is an access request; a requester who signs in before approval gets a clear status instead of "wrong password"
  const [review, setReview] = useState<"pending" | "rejected" | null>(null);
  const schema = z.object({ email: z.string().email(t("auth.invalidEmail")), password: z.string().min(8, t("auth.passwordRule")) });
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { email: "", password: "" } });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    setReview(null);
    try {
      const me = await api("login", { body: values });
      adoptSession(me);
      await navigate({ to: to && to.startsWith("/") && !to.startsWith("//") && to !== "/" ? to : "/home", replace: true });
    } catch (e) {
      if (isApiError(e) && (e.problem?.title === "account_pending" || e.problem?.title === "account_rejected")) return setReview(e.problem.title === "account_pending" ? "pending" : "rejected");
      setError(isApiError(e) && (e.status === 401 || e.status === 400) ? t("auth.badCredentials") : isApiError(e) && e.isNetwork ? t("common.errors.network") : t("common.states.error"));
    }
  });

  return (
    <AuthLayout>
      <h1 className="text-[28px] font-semibold tracking-tight text-ink">{t("auth.welcomeBack")}</h1>
      <p className="mt-2 text-[14px] text-ink-3">{t("auth.signInHint")}</p>

      {(expired || error) && (
        <div role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-bad-soft px-3.5 py-3 text-[13px] text-bad">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          {error ?? t("auth.sessionExpired")}
        </div>
      )}

      {review && (
        <div role="status" className={`mt-5 flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-[13px] ${review === "pending" ? "bg-accent-soft text-accent-ink" : "bg-bad-soft text-bad"}`} data-testid="login-review" data-state={review}>
          {review === "pending" ? <Hourglass className="mt-0.5 size-4 shrink-0" /> : <AlertCircle className="mt-0.5 size-4 shrink-0" />}
          <span>
            <span className="block font-semibold">{t(review === "pending" ? "access.login.pendingTitle" : "access.login.rejectedTitle")}</span>
            {t(review === "pending" ? "access.login.pending" : "access.login.rejected")}
          </span>
        </div>
      )}

      <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4">
        <Field label={t("auth.email")} error={form.formState.errors.email?.message} required>
          <Input type="email" autoComplete="email" dir="ltr" inputSize="lg" leading={<Mail />} placeholder="you@brand.com" {...form.register("email")} />
        </Field>
        <Field label={t("auth.password")} error={form.formState.errors.password?.message} required>
          <Input
            type={show ? "text" : "password"}
            autoComplete="current-password"
            dir="ltr"
            inputSize="lg"
            {...form.register("password")}
            trailing={
              <button type="button" onClick={() => setShow((s) => !s)} className="grid size-8 place-items-center rounded-lg text-ink-3 hover:text-ink" aria-label={show ? t("auth.hidePassword") : t("auth.showPassword")}>
                {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            }
          />
        </Field>
        <Button type="submit" variant="brand" size="lg" className="w-full" loading={form.formState.isSubmitting} iconEnd={<ArrowRight className="flip-rtl" />}>
          {form.formState.isSubmitting ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
      </form>

      {config().mock && (
        <button
          type="button"
          onClick={() => {
            form.setValue("email", DEMO.email, { shouldValidate: true });
            form.setValue("password", DEMO.password, { shouldValidate: true });
          }}
          className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-dashed border-line-2 px-4 py-3 text-start transition hover:border-accent hover:bg-accent-soft"
        >
          <span className="grid size-8 shrink-0 place-items-center rounded-xl brand-bg text-white">
            <Sparkles className="size-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold text-ink">{t("auth.useDemo")}</span>
            <span className="block truncate text-[12px] text-ink-3" dir="ltr">
              {DEMO.email} · {DEMO.password}
            </span>
          </span>
        </button>
      )}

      <p className="mt-8 text-center text-[13.5px] text-ink-3">
        {t("access.login.noAccount")}{" "}
        <Link to="/signup" className="font-semibold text-accent-ink hover:underline" data-testid="login-request-access">
          {t("access.login.request")}
        </Link>
      </p>
    </AuthLayout>
  );
}
